import React, { useEffect, useState } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import FormRating from '../ReactHookFormCustom/FormRating';
import FormTextArea from '../ReactHookFormCustom/FormTextArea';
import FormLabel from '../ReactHookFormCustom/FormLabel';
import { getQuestionsForQuestionnaireWithAnswers } from '../../Services/applicationService';
import { updateQuestionnaire } from '../../Services/applicationService';

const QuestionnaireModal = ({ item, showModal, comment, onToggle }) => {
  const { t } = useTranslation();
  const { handleSubmit, reset, control } = useForm({});
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState([]);

  // Reset form when comment changes
  useEffect(() => {
    reset({});
  }, [comment, reset]);

  // Load questions when the modal opens
  useEffect(() => {
    if (showModal) {
      setIsLoading(true);
      loadQuestions();
    }
  }, [showModal]);

  const loadQuestions = () => {
    getQuestionsForQuestionnaireWithAnswers(item.jobId, item.applicationId)
      .then((response) => {
        setQuestions(response.data);
      })
      .catch((error) => {
        console.error('Error loading questions:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Once questions are loaded, set the default form values.
  useEffect(() => {
    if (questions.length > 0) {
      const defaultValues = {};
      questions.forEach((question) => {
        const answer = question.answers[0];
        defaultValues['ratingForAnswer_' + answer.id] = answer.rating || '';
        defaultValues['messageForAnswer_' + answer.id] = answer.content || '';
      });
      reset(defaultValues);
    }
  }, [questions, reset]);

  const handleSubmitWithValidations = (values) => {
    setIsLoading(true);
    // Prepare payload: for each question, map the answer's rating and message from the form values
    const payload = {
      answers: questions.map((question) => {
        const answer = question.answers[0];
        return {
          questionId: question.id,
          applicationId: item.applicationId,
          jobId: item.jobId,
          answerId: answer.id,
          rating: values[`ratingForAnswer_${answer.id}`],
          message: values[`messageForAnswer_${answer.id}`],
        };
      }),
    };

    var combined = { ...payload, jobId: item.jobId, applicationId: item.applicationId };
    updateQuestionnaire(combined)
      .then((response) => {
        onToggle(response.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Modal isOpen={showModal} size="lg" centered>
      <form onSubmit={handleSubmit(handleSubmitWithValidations)}>
        <ModalHeader>
          <div className="d-flex justify-content-between w-100">
            <em className="fa-2x icon-bubbles mr-2">&nbsp;{t('jobs.QUESTIONNAIRE')}</em>
          </div>
        </ModalHeader>
        <ModalBody>
          {questions.length > 0
            ? questions.map((question, index) => (
                <span key={question.id}>
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-between align-items-center">
                        <FormLabel
                          control={control}
                          name={question.content}
                          id={question.id}
                          label={`${index + 1}. ${question.content}`}
                        />
                        <FormRating
                          control={control}
                          initialValue={question.answers[0]?.rating}
                          name={`ratingForAnswer_${question.answers[0]?.id}`}
                          id={question.answers[0]?.id}
                          size={25}
                          transition
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormTextArea
                        control={control}
                        name={`messageForAnswer_${question.answers[0]?.id}`}
                        id={`messageForAnswerId_${question.answers[0]?.id}`}
                        height="50px"
                        maxLength={3000}
                      />
                    </Col>
                  </Row>
                </span>
              ))
            : 'No questions added to Questionnaire'}
        </ModalBody>
        <ModalFooter>
          <div>
            {questions.length > 0 && (
              <Button color="primary" disabled={isLoading} className="mr-2" type="submit">
                {isLoading && <div className="spinner-border spinner-border-sm mr-2" role="status" />}
                {t('common.SAVE')}
              </Button>
            )}
            <Button color="secondary" onClick={onToggle}>
              {t('common.CANCEL')}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default QuestionnaireModal;
